import {
  FlexSpacer,
  Icon,
  Section,
  IconButton,
  Stack,
  Tooltip
} from '@efast_public/fjd-component-library';
import React from 'react';
import {
  NachrichtModel,
  VersandStatus
} from '../../../../../api/models/Nachrichten/NachrichtModel';
import _moment from 'moment';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import classes from './NachrichtItem.module.css';
import BescheidstatusBadge from '../../../../shared/BescheidstatusBadge/BescheidstatusBadge';
import { Bescheidstatus } from '../../../../../api/models/Status/Bescheidstatus';
import AntragBadge from '../../../../shared/AntragBadge/AntragBadge';
import VersandStatusBadge from '../../../../shared/VersandStatusBadge/VersandStatusBadge';
import { CaseDetailsModel } from '../../../../../api/models/Case/CaseDetailsModel';
import {
  useCancelVersand,
  useDeleteVersand,
  useRetryVersand,
  useSwitchToPostalVersand
} from '../../../../../api/hooks/useVersandActions';
import { useNotification } from '../../../../../shared/notification/useNotification';

const NachrichtItem = ({
  caseDetails,
  ...props
}: {
  readonly nachricht: NachrichtModel;
  readonly isSelected: boolean;
  readonly selectNachrichtHandler: (Nachricht: NachrichtModel) => void;
  readonly caseDetails: CaseDetailsModel;
  readonly openAbschliessenModal: () => void;
}) => {
  const { caseId } = useParams() as { caseId: string };
  const { t } = useTranslation();
  const { confirmByUser } = useNotification();
  const { cancelVersand, isCanceling } = useCancelVersand(caseId);
  const { retryVersand, isRetrying } = useRetryVersand(caseId);
  const { deleteVersand, isDeleting } = useDeleteVersand(caseId);
  const { switchToPostalVersand, isSwitchingToPostal } =
    useSwitchToPostalVersand(caseId);

  const confirmCloseCase = () => {
    confirmByUser({
      title: t('nachrichtItem.postalSwitch.closeCaseConfirmTitle'),
      content: t('nachrichtItem.postalSwitch.closeCaseConfirmContent'),
      confirmationButtonLabel: t(
        'nachrichtItem.postalSwitch.closeCaseConfirmButton'
      ),
      abortButtonLabel: t('nachrichtItem.postalSwitch.closeCaseAbortButton'),
      callback: () => props.openAbschliessenModal()
    });
  };

  const confirmSwitchToPostal = (submissionId: string) => {
    confirmByUser({
      title: t('nachrichtItem.postalSwitch.confirmTitle'),
      content: t('nachrichtItem.postalSwitch.confirmContent'),
      confirmationButtonLabel: t('nachrichtItem.postalSwitch.confirmButton'),
      abortButtonLabel: t('nachrichtItem.postalSwitch.abortButton'),
      callback: () =>
        switchToPostalVersand(submissionId).then(() => confirmCloseCase())
    });
  };

  const confirmCancelVersand = (
    submissionId: string,
    status: VersandStatus
  ) => {
    const isOverdue = status === VersandStatus.OVERDUE;
    confirmByUser({
      title: t('nachrichtItem.cancelVersand.confirmTitle'),
      content: isOverdue
        ? t('nachrichtItem.cancelVersand.confirmContentOverdue')
        : t('nachrichtItem.cancelVersand.confirmContent'),
      confirmationButtonLabel: t('nachrichtItem.cancelVersand.confirmButton'),
      abortButtonLabel: t('nachrichtItem.cancelVersand.abortButton'),
      callback: () => cancelVersand(submissionId)
    });
  };

  const confirmDeleteVersand = (submissionId: string) => {
    confirmByUser({
      title: t('nachrichtItem.deleteVersand.confirmTitle'),
      content: t('nachrichtItem.deleteVersand.confirmContent'),
      confirmationButtonLabel: t('nachrichtItem.deleteVersand.confirmButton'),
      abortButtonLabel: t('nachrichtItem.deleteVersand.abortButton'),
      callback: () => deleteVersand(submissionId)
    });
  };

  const clickHandler = () => {
    props.selectNachrichtHandler(props.nachricht);
  };
  const initialSubmissionId: string =
    caseDetails.initialSubmission.submissionId;

  const cssClass = props.isSelected
    ? classes.selectedNachricht
    : classes.notSelectedNachricht;
  const showBescheidBadge: boolean =
    props.nachricht.isBescheid &&
    (caseDetails.bescheidstatus === Bescheidstatus.POSITIV ||
      caseDetails.bescheidstatus === Bescheidstatus.NEGATIV);

  const versandStatus = props.nachricht.versandStatus;
  const isBusy = isCanceling || isRetrying || isDeleting || isSwitchingToPostal;

  // TODO: Rework this
  return (
    <Section key={Math.random()}>
      <Section.Content>
        <div
          id={`nachrichtContainer_${props.nachricht.submissionId}`}
          className={
            !props.nachricht.hasBeenRead
              ? `${classes.nachrichtContainer} ${classes.unread}`
              : classes.nachrichtContainer
          }
        >
          <div
            role="button"
            tabIndex={0}
            className={cssClass}
            onClick={clickHandler}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                props.selectNachrichtHandler(props.nachricht);
              }
            }}
          >
            <Stack direction={'column'} spacing="xxs">
              <Stack direction={'row'} spacing={'m'}>
                <div
                  id={`betreff_${props.nachricht.submissionId}`}
                  className={classes.betreff}
                >
                  {props.nachricht.betreff}
                </div>
                <FlexSpacer />
                <Stack direction={'row'} className={classes.topRightIcons}>
                  {versandStatus && (
                    <span className={`${classes.iconAlign}`}>
                      <VersandStatusBadge
                        status={versandStatus.status}
                        channel={versandStatus.channel}
                      />
                    </span>
                  )}

                  {props.nachricht.hasAttachments && (
                    <span
                      id={`hatAnlage_${props.nachricht.submissionId}`}
                      className={`${classes.iconAlign}`}
                    >
                      <Tooltip placement="auto" tooltip="Hat Anlagen">
                        <span>
                          <Icon name="link" size="sm" />
                        </span>
                      </Tooltip>
                    </span>
                  )}
                </Stack>
              </Stack>
              <Stack direction={'row'} spacing={'m'} alignItems="center">
                <Icon
                  size={'md'}
                  name={props.nachricht.isVonBehoerde ? 'reply' : 'email'}
                ></Icon>
                <div
                  id={`bescheid_${props.nachricht.submissionId}`}
                  className={
                    props.nachricht.isVonBehoerde
                      ? classes.vonBescheidNegative
                      : classes.vonBescheidPositive
                  }
                >
                  <span>
                    {props.nachricht.isVonBehoerde
                      ? `von ${props.nachricht.absenderName}`
                      : 'von Antragssystem'}
                  </span>
                </div>
              </Stack>
              <Stack direction={'row'} spacing={'m'}>
                <Stack direction={'row'} spacing={'m'} alignItems={'center'}>
                  <div className={`${classes.iconAlign}`}>
                    {_moment
                      .utc(props.nachricht.createdOn)
                      .local()
                      .format('DD.MM.YYYY • HH:mm')}
                  </div>
                  {showBescheidBadge && (
                    <BescheidstatusBadge
                      bescheidstatus={caseDetails.bescheidstatus}
                      fjdBadgeSize={'xs'}
                      badgeLabel={'Bescheid'}
                    />
                  )}
                  {props.nachricht.submissionId === initialSubmissionId && (
                    <AntragBadge data-testid="antragBadge" />
                  )}
                </Stack>
                <FlexSpacer />
                {versandStatus &&
                  (() => {
                    const hasCancel =
                      versandStatus.status === VersandStatus.SCHEDULED ||
                      versandStatus.status === VersandStatus.DISPATCHING ||
                      versandStatus.status === VersandStatus.OVERDUE;
                    const hasRetry =
                      versandStatus.status === VersandStatus.FAILED;
                    const hasDelete =
                      versandStatus.status === VersandStatus.FAILED ||
                      versandStatus.status === VersandStatus.CANCELED;
                    const hasPostalSwitch = versandStatus.postalSwitchable;
                    const hasActions =
                      hasCancel || hasRetry || hasDelete || hasPostalSwitch;
                    return (
                      <span
                        style={{
                          visibility: hasActions ? 'visible' : 'hidden'
                        }}
                      >
                        <Stack direction={'row'} spacing={'xs'}>
                          {hasCancel && (
                            <Tooltip placement="auto" tooltip="Abbrechen">
                              <IconButton
                                id={`versand_cancel_${props.nachricht.submissionId}`}
                                size={'sm'}
                                icon={'close'}
                                label={'Abbrechen'}
                                variant={'primary-text'}
                                disabled={isBusy}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmCancelVersand(
                                    props.nachricht.submissionId,
                                    versandStatus.status
                                  );
                                }}
                              />
                            </Tooltip>
                          )}
                          {hasRetry && (
                            <Tooltip
                              placement="auto"
                              tooltip="Erneut versenden"
                            >
                              <IconButton
                                id={`versand_retry_${props.nachricht.submissionId}`}
                                size={'sm'}
                                icon={'restart'}
                                label={'Erneut versenden'}
                                variant={'primary-text'}
                                disabled={isBusy}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  retryVersand(props.nachricht.submissionId);
                                }}
                              />
                            </Tooltip>
                          )}
                          {hasPostalSwitch && (
                            <Tooltip
                              placement="auto"
                              tooltip="Postalischen Versand im System hinterlegen"
                            >
                              <IconButton
                                id={`versand_postal_${props.nachricht.submissionId}`}
                                size={'sm'}
                                icon={'email'}
                                label={
                                  'Postalischen Versand im System hinterlegen'
                                }
                                variant={'primary-text'}
                                disabled={isBusy}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmSwitchToPostal(
                                    props.nachricht.submissionId
                                  );
                                }}
                              />
                            </Tooltip>
                          )}
                          {hasDelete && (
                            <Tooltip placement="auto" tooltip="Löschen">
                              <IconButton
                                id={`versand_delete_${props.nachricht.submissionId}`}
                                size={'sm'}
                                icon={'trashcan'}
                                label={'Löschen'}
                                variant={'primary-text'}
                                disabled={isBusy}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDeleteVersand(
                                    props.nachricht.submissionId
                                  );
                                }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </span>
                    );
                  })()}
              </Stack>
            </Stack>
          </div>
        </div>
      </Section.Content>
    </Section>
  );
};

export default NachrichtItem;
